/**
 * @fileoverview The link that travels with a share: its code made on the
 * device once per set, layout and size (newShareCode), so Save and Share
 * carry the same code at once; saved at its first use (POST /shares), its use
 * recorded (POST /shares/:code/used) and the pictures put behind it (POST
 * /shares/:code/frame/:i). Fire and forget: a failure on our side is reported
 * to Sentry and nothing is said to the sharer. A member tending someone
 * else's garden gets no link (story.tending): only the garden's owner makes
 * one (B37; the server answers a member 403 OWNER_ONLY). Moved out of
 * plant-list/components/dialogs/SharePlantDialog (batch 18, A105); nothing in
 * it changed.
 *
 * Where: SharePlantDialog (and its share and save, shareOut).
 * API: POST /shares (a season's with season, soFar, isPublic and layout
 * 'season'), POST /shares/:code/used, POST /shares/:code/frame/:i?count=.
 *
 * @module useShareLink
 */

import React from 'react';
import { markShareUsed, mintShare, newShareCode, shareUrlFor, uploadShareFrames } from '@/shared/api/shares';
import { isServerFailure, reportServerFailure } from '@/shared/lib/serverFailure';
import type { SharePlantDialogProps } from './shareStudioTypes';
import type { ShareStory } from './useShareStory';

/**
 * Reports a fire-and-forget request's failure when it was on our side (D31);
 * the share itself has gone out, so nothing is said to the sharer.
 *
 * @param err - What the request threw.
 */
const reportIfOurs = (err: unknown): void => {
  if (isServerFailure(err)) reportServerFailure(err, 'share.link');
};

/**
 * The share's link, and what saves it as it is used.
 *
 * @param props - The dialog's props (SharePlantDialogProps).
 * @param story - The story and the choices (useShareStory).
 * @param frames - The finished pictures, or null while they are made.
 * @returns The link (null until there are pictures to send) and used, which saves it.
 */
export function useShareLink(
  props: SharePlantDialogProps, story: ShareStory, frames: File[] | null,
): { link: string | null; used: (action: 'share' | 'save' | 'copy') => void } {
  const { isOpen, plantObjId, season } = props;
  const { subject, style, layout, order, selectionKey, nothingToSend, sharedWords, tending } = story;

  /*
   * The link that travels with the share.
   *
   * Its code is made here (newShareCode), once per set, layout and size, so
   * Save, Copy and Share all carry the same code and the link can be shown
   * and put in the share text at once: the share sheet only opens while the
   * tap is live, and waiting for the server in between is what costs it.
   * Nothing is saved until the link is first used: opening the sheet to look
   * and closing it leaves no link behind (it used to save one on opening,
   * which a bed's page could then link to, with no pictures behind it).
   */
  const mintKey = `${selectionKey}|${layout}|${style.size}`;
  // A new code for each set, layout and size, and each time the sheet opens.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const code = React.useMemo(() => newShareCode(), [mintKey, isOpen]);
  /** The link that goes with the share, once there are pictures to send; none in someone else's garden (B37). */
  const link = frames && !nothingToSend && !tending ? shareUrlFor(code) : null;
  /** The link saved, once for this code, at its first use. */
  const saving = React.useRef<{ code: string; done: Promise<unknown> } | null>(null);
  /**
   * Saves the link (POST /shares, once per code), records how it went out
   * (POST /shares/:code/used), and puts the pictures behind it (POST
   * /shares/:code/frame/:i). Fire and forget: a failure leaves a poorer
   * share, never a failed one.
   *
   * @param action - How it went out: the share sheet, saved, or copied.
   */
  const used = (action: 'share' | 'save' | 'copy'): void => {
    if (!link) return;
    if (saving.current?.code !== code) {
      saving.current = {
        code,
        // A season's link is the season's, its photos by their own ids.
        done: season
          ? mintShare({
            season: season.year, soFar: season.soFar, isPublic: season.isPublic, layout: 'season', size: style.size, code,
            photoIds: order.map((id) => -id), words: sharedWords || null,
          })
          : mintShare({ ...(subject.kind === 'location' ? { locationId: subject.id } : { plantObjId }), photoIds: order, layout, size: style.size, code }),
      };
    }
    const shared = frames;
    saving.current.done
      .then(async () => {
        void markShareUsed(code, action, sharedWords || null).catch(reportIfOurs);
        // The pictures that went out, so the page behind the link shows them.
        if (shared && shared.length > 0) await uploadShareFrames(code, shared);
        season?.onShared?.();
      })
      // No link saved is a poorer share, not a failed one; a failure on our
      // side is still reported (D31).
      .catch(reportIfOurs);
  };

  return { link, used };
}
